"use client";

import {
  SITE_NAVIGATION_ALIGNMENTS,
  SITE_NAVIGATION_OPEN_MODES,
  SITE_NAVIGATION_SOURCES,
  SITE_NAVIGATION_THEMES,
  SITE_NAVIGATION_VARIANT_DEFINITIONS,
  SITE_NAVIGATION_VARIANTS,
  isSiteNavigationAlignment,
  isSiteNavigationOpenMode,
  isSiteNavigationSource,
  isSiteNavigationTheme,
  resolveSiteNavigationSettings,
  resolveSiteNavigationThemeTokens,
  siteNavigationThemeStyle,
  withSiteNavigationSettings,
  type SiteBuilderBlock,
  type SiteBuilderDesign,
  type SiteNavigationItem,
  type SiteNavigationSettings,
  type SiteNavigationTheme,
  type SiteNavigationThemeTokens,
  type SiteNavigationVariant,
  type SiteNavigationViewport,
} from "@webdo/site-templates/builder";
import {
  SiteNavigation,
  SiteNavigationStyles,
} from "@webdo/site-templates";
import {
  mergeTemplateDesign,
  templateDesignToCssVars,
} from "@webdo/site-templates/builder-core";
import { useMemo, type CSSProperties } from "react";
import {
  BuilderNavigationVariantPicker,
} from "./BuilderNavigationVariantPicker";
import {
  builderNavigationVariantOptions,
  type BuilderNavigationSelectControl,
  type BuilderNavigationSelectOption,
  type BuilderNavigationVariantPickerAction,
} from "./navigationVariantPickerModel";

export type BuilderNavigationBlockInspectorTranslator = (
  key: string,
) => string;

export type BuilderNavigationMenuLocationOption = {
  label: string;
  value: string;
};

const GLASS_PREVIEW_CANVAS_BACKGROUND = [
  "linear-gradient(135deg,",
  "color-mix(in srgb, var(--site-navigation-accent) 9%, var(--site-background)) 0 42%,",
  "var(--site-background) 42% 58%,",
  "color-mix(in srgb, var(--site-navigation-text) 6%, var(--site-surface)) 58% 100%)",
].join(" ");

const DEFAULT_NAVIGATION_PREVIEW_MODES: readonly SiteNavigationViewport[] = [
  "desktop",
];

function previewForeground(theme: SiteNavigationTheme): string {
  if (theme === "accent") return "var(--site-on-primary)";
  if (theme === "inverse") return "var(--site-on-inverse)";

  return "var(--site-on-surface)";
}

function previewAccentForeground(theme: SiteNavigationTheme): string {
  if (theme === "accent") return "var(--site-on-surface)";
  if (theme === "inverse") return "var(--site-on-accent)";

  return "var(--site-on-primary)";
}

/**
 * The inspector is rendered outside the site canvas, so inherited `--site-*`
 * variables are not available there. Keep the production palette and anatomy,
 * but resolve semantic foregrounds locally so inverse, accent and glass tiles
 * never fall back to white on the picker's former white canvas.
 */
export function builderNavigationPreviewThemeTokens(
  design: SiteBuilderDesign,
  theme: SiteNavigationTheme,
): SiteNavigationThemeTokens {
  const tokens = resolveSiteNavigationThemeTokens(design, theme);
  const text = previewForeground(theme);

  return {
    ...tokens,
    accentText: previewAccentForeground(theme),
    border: `color-mix(in srgb, ${text} 24%, ${tokens.background})`,
    muted: `color-mix(in srgb, ${text} 70%, ${tokens.background})`,
    text,
  };
}

export function builderNavigationPreviewCanvasBackground(
  theme: SiteNavigationTheme,
): string {
  return theme === "glass"
    ? GLASS_PREVIEW_CANVAS_BACKGROUND
    : "var(--site-navigation-background)";
}

function sampleItem(
  uuid: string,
  label: string,
  children: SiteNavigationItem[] = [],
): SiteNavigationItem {
  return {
    children,
    href: `#navigation-preview-${uuid}`,
    label,
    openInNewTab: false,
    type: children.length > 0 ? "group" : "custom",
    uuid: `navigation-preview-${uuid}`,
  };
}

function sampleItems(
  translate: BuilderNavigationBlockInspectorTranslator,
): SiteNavigationItem[] {
  return [
    sampleItem("primary", translate("preview.primary"), [
      sampleItem("group-one", translate("preview.groupOne"), [
        sampleItem("item-one", translate("preview.itemOne")),
        sampleItem("item-two", translate("preview.itemTwo")),
      ]),
      sampleItem("group-two", translate("preview.groupTwo"), [
        sampleItem("feature-title", translate("preview.featureTitle")),
        sampleItem("feature-text", translate("preview.featureText")),
      ]),
      sampleItem("group-three", translate("preview.secondary"), [
        sampleItem("group-three-item-one", translate("preview.itemOne")),
        sampleItem("group-three-item-two", translate("preview.featureTitle")),
      ]),
      sampleItem("group-four", translate("preview.tertiary"), [
        sampleItem("group-four-item-one", translate("preview.itemTwo")),
        sampleItem("group-four-item-two", translate("preview.groupOne")),
      ]),
      sampleItem("group-five", translate("preview.itemOne"), [
        sampleItem("group-five-item-one", translate("preview.groupTwo")),
        sampleItem("group-five-item-two", translate("preview.itemTwo")),
      ]),
      sampleItem("group-six", translate("preview.itemTwo"), [
        sampleItem("group-six-item-one", translate("preview.featureTitle")),
        sampleItem("group-six-item-two", translate("preview.groupOne")),
      ]),
      sampleItem("group-seven", translate("preview.featureTitle"), [
        sampleItem("group-seven-item-one", translate("preview.itemOne")),
        sampleItem("group-seven-item-two", translate("preview.groupTwo")),
      ]),
    ]),
    sampleItem("secondary", translate("preview.secondary"), [
      sampleItem("secondary-item-one", translate("preview.itemOne")),
      sampleItem("secondary-item-two", translate("preview.itemTwo")),
      sampleItem("secondary-item-three", translate("preview.groupOne")),
      sampleItem("secondary-item-four", translate("preview.groupTwo")),
    ]),
    sampleItem("tertiary", translate("preview.tertiary")),
  ];
}

function selectOptions(
  values: readonly string[],
  translate: BuilderNavigationBlockInspectorTranslator,
  namespace: string,
): BuilderNavigationSelectOption[] {
  return values.map((value) => ({
    label: translate(`${namespace}.${value}`),
    value,
  }));
}

function uniqueLocationOptions(
  locations: readonly BuilderNavigationMenuLocationOption[],
  currentLocation: string,
): BuilderNavigationMenuLocationOption[] {
  const options = locations.filter((location, index) => (
    location.value.trim() !== ""
    && locations.findIndex((candidate) => (
      candidate.value.toLowerCase() === location.value.toLowerCase()
    )) === index
  ));

  if (!options.some((option) => option.value === currentLocation)) {
    options.push({ label: currentLocation, value: currentLocation });
  }

  return options;
}

function navigationItemRows(
  items: readonly SiteNavigationItem[],
  depth = 0,
): Array<{ depth: number; item: SiteNavigationItem }> {
  return items.flatMap((item) => [
    { depth, item },
    ...navigationItemRows(item.children, depth + 1),
  ]);
}

export function BuilderNavigationBlockInspector({
  actions = [],
  block,
  canEditItem,
  design,
  itemsDescription,
  menuLocations,
  onChange,
  onEditItem,
  previewModes = DEFAULT_NAVIGATION_PREVIEW_MODES,
  previewItems,
  settingsDefaults,
  translate,
  useFallbackPreviewItems = true,
  variants = SITE_NAVIGATION_VARIANTS,
}: {
  actions?: readonly BuilderNavigationVariantPickerAction[];
  block: SiteBuilderBlock;
  canEditItem?: (item: SiteNavigationItem) => boolean;
  design: SiteBuilderDesign;
  itemsDescription?: string;
  menuLocations: readonly BuilderNavigationMenuLocationOption[];
  onChange: (block: SiteBuilderBlock) => void;
  onEditItem?: (item: SiteNavigationItem) => Promise<void> | void;
  previewModes?: readonly SiteNavigationViewport[];
  previewItems?: readonly SiteNavigationItem[];
  settingsDefaults?: Partial<SiteNavigationSettings>;
  translate: BuilderNavigationBlockInspectorTranslator;
  useFallbackPreviewItems?: boolean;
  variants?: readonly SiteNavigationVariant[];
}) {
  const settings = resolveSiteNavigationSettings(block, settingsDefaults);
  const fallbackItems = useMemo(
    () => useFallbackPreviewItems ? sampleItems(translate) : [],
    [translate, useFallbackPreviewItems],
  );
  const items = previewItems && previewItems.length > 0
    ? previewItems
    : fallbackItems;
  const itemRows = navigationItemRows(items);
  const previewOpenItemUuid = items.find((item) => (
    item.children.length > 0
  ))?.uuid;
  const desktopLocations = uniqueLocationOptions(
    menuLocations,
    settings.menuLocation,
  );
  const mobileLocations = [
    { label: translate("locations.inherit"), value: "" },
    ...uniqueLocationOptions(
      menuLocations,
      settings.mobileMenuLocation ?? settings.menuLocation,
    ),
  ];
  const commit = (
    patch: Parameters<typeof withSiteNavigationSettings>[1],
  ): void => {
    const next = withSiteNavigationSettings(block, patch);

    if (next !== block) onChange(next);
  };
  const controls: BuilderNavigationSelectControl[] = [
    {
      key: "source",
      label: translate("fields.source"),
      onChange: (value) => {
        if (isSiteNavigationSource(value)) commit({ source: value });
      },
      options: selectOptions(SITE_NAVIGATION_SOURCES, translate, "sources"),
      value: settings.source,
    },
    {
      key: "theme",
      label: translate("fields.theme"),
      onChange: (value) => {
        if (isSiteNavigationTheme(value)) commit({ theme: value });
      },
      options: selectOptions(SITE_NAVIGATION_THEMES, translate, "themes"),
      value: settings.theme,
    },
    {
      key: "alignment",
      label: translate("fields.alignment"),
      onChange: (value) => {
        if (isSiteNavigationAlignment(value)) commit({ alignment: value });
      },
      options: selectOptions(
        SITE_NAVIGATION_ALIGNMENTS,
        translate,
        "alignments",
      ),
      value: settings.alignment,
    },
    {
      key: "openMode",
      label: translate("fields.openMode"),
      onChange: (value) => {
        if (isSiteNavigationOpenMode(value)) commit({ openMode: value });
      },
      options: selectOptions(
        SITE_NAVIGATION_OPEN_MODES,
        translate,
        "openModes",
      ),
      value: settings.openMode,
    },
    {
      key: "menuLocation",
      label: translate("fields.menuLocation"),
      onChange: (value) => commit({ menuLocation: value }),
      options: desktopLocations,
      value: settings.menuLocation,
    },
    {
      key: "mobileMenuLocation",
      label: translate("fields.mobileMenuLocation"),
      onChange: (value) => commit({
        mobileMenuLocation: value === "" ? null : value,
      }),
      options: mobileLocations,
      value: settings.mobileMenuLocation ?? "",
    },
  ];
  const availableVariants = variants.map(
    (variant) => SITE_NAVIGATION_VARIANT_DEFINITIONS[variant].variant,
  );
  const variantOptions = builderNavigationVariantOptions(
    availableVariants,
    (variant) => translate(`options.${variant}.label`),
    (variant) => translate(`options.${variant}.description`),
  );
  const previewDesign = mergeTemplateDesign(design);
  const previewDesignStyle = templateDesignToCssVars(previewDesign);

  return (
    <>
      <SiteNavigationStyles />
      <BuilderNavigationVariantPicker<SiteNavigationVariant>
        actions={actions}
        actionsLabel={translate("actionsLabel")}
        badge={translate("globalBadge")}
        controls={controls}
        description={translate("description")}
        label={translate("label")}
        onChange={(variant) => commit({ variant })}
        options={variantOptions}
        renderPreview={(variant) => {
          const previewSettings = { ...settings, variant };
          const previewStyle = siteNavigationThemeStyle(
            builderNavigationPreviewThemeTokens(
              previewDesign,
              previewSettings.theme,
            ),
          );

          return (
            <div
              className="relative h-full overflow-hidden"
              data-builder-navigation-preview-canvas="true"
              data-builder-navigation-preview-theme={previewSettings.theme}
              style={{
                ...previewDesignStyle,
                ...previewStyle,
                background: builderNavigationPreviewCanvasBackground(
                  previewSettings.theme,
                ),
                color: "var(--site-navigation-text)",
                fontFamily: previewDesign.typography.body,
              } as CSSProperties & Record<string, string | undefined>}
            >
              {items.length === 0 ? (
                <div
                  className="grid h-full place-items-center px-3 text-center text-[10px] font-semibold leading-4"
                  data-builder-navigation-empty-preview="true"
                >
                  {translate("preview.empty")}
                </div>
              ) : (
                <div
                  className={previewModes.length > 1
                    ? "grid h-full grid-cols-2 divide-x divide-white/10"
                    : "h-full"}
                  data-builder-navigation-preview-modes={previewModes.join(",")}
                >
                  {previewModes.map((mode) => (
                    <div
                      className="relative h-full min-w-0 overflow-hidden"
                      data-builder-navigation-preview-mode={mode}
                      key={mode}
                    >
                      {previewModes.length > 1 ? (
                        <span className="absolute right-1 top-1 z-10 rounded bg-surface-hover px-1 py-0.5 text-[6px] font-bold uppercase tracking-wide text-text/85">
                          {translate(`preview.${mode}`)}
                        </span>
                      ) : null}
                      <div
                        className="origin-top-left"
                        style={previewModes.length > 1
                          ? {
                            "--site-container": mode === "desktop"
                              ? "420px"
                              : "320px",
                            transform: mode === "desktop"
                              ? "scale(0.28)"
                              : "scale(0.24)",
                            width: mode === "desktop"
                              ? "357.1429%"
                              : "416.6667%",
                          } as CSSProperties & Record<string, string>
                          : {
                            "--site-container": "500px",
                            transform: "scale(0.58)",
                            width: "172.4138%",
                          } as CSSProperties & Record<string, string>}
                      >
                        <SiteNavigation
                          className="builder-navigation-production-preview"
                          includeStyles={false}
                          items={items}
                          labels={{
                            navigation: translate("preview.navigation"),
                            showAll: translate("preview.showAll"),
                          }}
                          mode={mode}
                          previewOpenItemUuid={previewOpenItemUuid}
                          readOnly
                          settings={previewSettings}
                          staticPreview
                          style={previewStyle}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }}
        settingsLabel={translate("settingsLabel")}
        value={settings.variant}
      />
      {itemsDescription || onEditItem ? (
        <section
          className="my-1.5 overflow-hidden rounded-lg border border-border bg-surface shadow-sm"
          data-builder-navigation-items="true"
        >
          <div className="border-b border-border px-2.5 py-2">
            <p className="text-[11px] font-semibold text-text">
              {translate("items.title")}
            </p>
            {itemsDescription ? (
              <p className="mt-0.5 text-[9px] leading-3.5 text-text-secondary">
                {itemsDescription}
              </p>
            ) : null}
          </div>
          <div className="grid max-h-72 gap-1.5 overflow-y-auto bg-surface p-2.5">
            {itemRows.length === 0 ? (
              <div className="rounded-md border border-dashed border-border px-2.5 py-3 text-center text-[10px] leading-4 text-text-secondary">
                {translate("items.empty")}
              </div>
            ) : itemRows.map(({ depth, item }) => {
              const editable = onEditItem !== undefined
                && (canEditItem?.(item) ?? true);
              const content = (
                <>
                  <span className="min-w-0">
                    <span className="block truncate text-[10px] font-semibold text-text">
                      {item.label}
                    </span>
                    <code className="mt-0.5 block truncate font-mono text-[9px] text-primary/80">
                      {item.href?.trim() || translate("items.noEndpoint")}
                    </code>
                  </span>
                  {editable ? (
                    <span className="text-[9px] font-semibold text-text-secondary transition group-hover:text-primary">
                      {translate("items.edit")}
                    </span>
                  ) : null}
                </>
              );
              const itemStyle = {
                marginLeft: `${Math.min(depth, 3) * 0.65}rem`,
              };

              return editable ? (
                <button
                  className="group grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-2 text-left transition hover:border-primary/50 hover:bg-surface"
                  data-builder-navigation-item={item.uuid}
                  key={item.uuid}
                  onClick={() => void onEditItem?.(item)}
                  style={itemStyle}
                  type="button"
                >
                  {content}
                </button>
              ) : (
                <div
                  className="grid min-w-0 grid-cols-[minmax(0,1fr)] items-center rounded-md border border-border bg-surface px-2.5 py-2 text-left"
                  data-builder-navigation-item={item.uuid}
                  key={item.uuid}
                  style={itemStyle}
                >
                  {content}
                </div>
              );
            })}
          </div>
        </section>
      ) : null}
    </>
  );
}
